import { useCompanyAssessmentResults } from 'Features/Results/Results.hooks';
import { useAllActivities } from 'Features/Screening/Screening.hooks';
import { groupBy, padStart } from 'lodash';
import { ActivityResults, CompanyAssessmentResults, ObjectiveKeyEnum } from 'models';
import { useMemo } from 'react';
import { useCurrentCompany } from 'utils/hooks';
import {
  RowFormat,
  ActivitiesSheetFormat,
  TaxonomyTableData,
  SCEligibility,
} from './table-generator';
import { calculateKpiSheetData, getAlignedSumRow, getTotalRow } from './TaxonomyTableTotals';
import {
  getEligibleAlignedActivities,
  getEligibleNotAlignedActivities,
} from './TaxonomyTableActivities';
import { stringToYear } from 'utils/date';

export type ResultsByActivity = { [key: string]: ActivityResults[] };
export type ResultsBySC = { [key: string]: ActivityResults[] };
export type ResultsByActivityAndSC = { [key: string]: ResultsBySC };
export type FinancialsAttribute =
  | 'aligned'
  | 'eligible'
  | 'inProgress'
  | 'notAligned'
  | 'notEligible'
  | 'total';

export type Financials = {
  [attribute in FinancialsAttribute]: number;
};

export type FinancialsType = 'capex' | 'opex' | 'revenue';

export type FinancialsPerType = {
  [type in FinancialsType]: Financials;
};

const defaultFinancialsPerType: Financials = {
  aligned: 0,
  eligible: 0,
  inProgress: 0,
  notAligned: 0,
  notEligible: 0,
  total: 0,
};

const defaultFinancials: FinancialsPerType = {
  capex: defaultFinancialsPerType,
  opex: defaultFinancialsPerType,
  revenue: defaultFinancialsPerType,
};

export const getProportion = (absolute: number, total?: number): number => {
  if (total && total > 0) {
    return absolute / total;
  } else {
    return 0;
  }
};

const getRefNumbers = (input?: string): number[] | null => {
  if (!input) return null;
  const re = /(\d+)\.(\d+)(?:\.(\d+))?/;
  const matches = input.match(re);
  if (matches) {
    return [parseInt(matches[1] ?? '0'), parseInt(matches[2] ?? '0'), parseInt(matches[3] ?? '0')];
  } else {
    return null;
  }
};

const sortByRef = (row: RowFormat): string => {
  const refNumbers = getRefNumbers(row.activityName);
  if (!refNumbers) return row.activityName ?? '';
  const firstNumber = padStart(`${refNumbers[0]}`, 3, '0');
  const secondNumber = padStart(`${refNumbers[1]}`, 3, '0');
  const thirdNumber = padStart(`${refNumbers[2]}`, 3, '0');
  return `${firstNumber}.${secondNumber}.${thirdNumber}`;
};

function consolidateCode({
  newCode,
  existingCode,
}: {
  newCode: string;
  existingCode: string;
}): string {
  const codes = [
    ...existingCode
      .split(',')
      .map((c) => c.trim())
      .filter(Boolean),
    ...newCode
      .split(',')
      .map((c) => c.trim())
      .filter(Boolean),
  ];

  const uniqueCodes = Array.from(new Set(codes));
  return uniqueCodes.join(', ');
}

const calculateSheet = (
  resultsByActivityAndTag: ResultsByActivityAndSC,
  activities: any,
  totalFinancials: FinancialsPerType,
  type: FinancialsType
): ActivitiesSheetFormat => {
  const eligibleAlignedActivities = getEligibleAlignedActivities(
    resultsByActivityAndTag,
    type,
    activities,
    totalFinancials[type].total
  );

  const eligibleNotAlignedActivities = getEligibleNotAlignedActivities(
    resultsByActivityAndTag,
    type,
    activities,
    totalFinancials[type].total
  );

  const allEligibleActivities: RowFormat[] = [
    ...eligibleAlignedActivities,
    ...eligibleNotAlignedActivities,
  ];

  const consolidatedActivities: RowFormat[] = Object.values(
    allEligibleActivities.reduce(
      (acc, activity) => {
        const activityName: any = activity.activityName;
        if (!!activityName) {
          acc[activityName] = {
            activityName,
            code: consolidateCode({
              newCode: activity.code ?? '',
              existingCode: acc[activityName]?.code ?? '',
            }),
            eligiblePct: (acc[activityName]?.eligiblePct ?? 0) + (activity.eligiblePct ?? 0),
            alignedAbsolute:
              (acc[activityName]?.alignedAbsolute ?? 0) + (activity.alignedAbsolute ?? 0),
            alignedPct: (acc[activityName]?.alignedPct ?? 0) + (activity.alignedPct ?? 0),
            mitigationSC: {
              financial:
                (acc[activityName]?.mitigationSC?.financial ?? 0) +
                (activity.mitigationSC?.financial ?? 0),
              eligibility:
                activity.mitigationSC?.eligibility === SCEligibility.nonEligible ||
                acc[activityName]?.mitigationSC?.eligibility === SCEligibility.nonEligible
                  ? SCEligibility.nonEligible
                  : SCEligibility.eligible,
              doubleCountedFinancial:
                (acc[activityName]?.mitigationSC?.doubleCountedFinancial ?? 0) +
                (activity.mitigationSC?.doubleCountedFinancial ?? 0),
            },
            adaptationSC: {
              financial:
                (acc[activityName]?.adaptationSC?.financial ?? 0) +
                (activity.adaptationSC?.financial ?? 0),
              eligibility:
                activity.adaptationSC?.eligibility === SCEligibility.nonEligible ||
                acc[activityName]?.adaptationSC?.eligibility === SCEligibility.nonEligible
                  ? SCEligibility.nonEligible
                  : SCEligibility.eligible,
              doubleCountedFinancial:
                (acc[activityName]?.adaptationSC?.doubleCountedFinancial ?? 0) +
                (activity.adaptationSC?.doubleCountedFinancial ?? 0),
            },
            waterSC: {
              financial:
                (acc[activityName]?.waterSC?.financial ?? 0) + (activity.waterSC?.financial ?? 0),
              eligibility:
                activity.waterSC?.eligibility === SCEligibility.nonEligible ||
                acc[activityName]?.waterSC?.eligibility === SCEligibility.nonEligible
                  ? SCEligibility.nonEligible
                  : SCEligibility.eligible,
              doubleCountedFinancial:
                (acc[activityName]?.waterSC?.doubleCountedFinancial ?? 0) +
                (activity.waterSC?.doubleCountedFinancial ?? 0),
            },
            pollutionSC: {
              financial:
                (acc[activityName]?.pollutionSC?.financial ?? 0) +
                (activity.pollutionSC?.financial ?? 0),
              eligibility:
                activity.pollutionSC?.eligibility === SCEligibility.nonEligible ||
                acc[activityName]?.pollutionSC?.eligibility === SCEligibility.nonEligible
                  ? SCEligibility.nonEligible
                  : SCEligibility.eligible,
              doubleCountedFinancial:
                (acc[activityName]?.pollutionSC?.doubleCountedFinancial ?? 0) +
                (activity.pollutionSC?.doubleCountedFinancial ?? 0),
            },
            circularSC: {
              financial:
                (acc[activityName]?.circularSC?.financial ?? 0) +
                (activity.circularSC?.financial ?? 0),
              eligibility:
                activity.circularSC?.eligibility === SCEligibility.nonEligible ||
                acc[activityName]?.circularSC?.eligibility === SCEligibility.nonEligible
                  ? SCEligibility.nonEligible
                  : SCEligibility.eligible,
              doubleCountedFinancial:
                (acc[activityName]?.circularSC?.doubleCountedFinancial ?? 0) +
                (activity.circularSC?.doubleCountedFinancial ?? 0),
            },
            biodiversitySC: {
              financial:
                (acc[activityName]?.biodiversitySC?.financial ?? 0) +
                (activity.biodiversitySC?.financial ?? 0),
              eligibility:
                activity.biodiversitySC?.eligibility === SCEligibility.nonEligible ||
                acc[activityName]?.biodiversitySC?.eligibility === SCEligibility.nonEligible
                  ? SCEligibility.nonEligible
                  : SCEligibility.eligible,
              doubleCountedFinancial:
                (acc[activityName]?.biodiversitySC?.doubleCountedFinancial ?? 0) +
                (activity.biodiversitySC?.doubleCountedFinancial ?? 0),
            },
            enabling: (acc[activityName]?.enabling ?? 0) + (activity.enabling ?? 0),
            transitional: (acc[activityName]?.transitional ?? 0) + (activity.transitional ?? 0),
            alignedPctOfEligible: 0, //calculated later,
          };
        }
        return acc;
      },
      {} as Record<string, RowFormat>
    )
  );

  consolidatedActivities.sort((a, b) => {
    return sortByRef(a).localeCompare(sortByRef(b));
  });

  consolidatedActivities.forEach((activity) => {
    activity.eligiblePct = (activity?.eligiblePct ?? 0) / totalFinancials[type].total;
    activity.alignedPctOfEligible =
      activity.eligiblePct && activity.eligiblePct > 0
        ? (activity.alignedAbsolute ?? 0) /
          ((activity.eligiblePct ?? 0) * totalFinancials[type].total)
        : 0;
  });

  const alignedSum: RowFormat = getAlignedSumRow(consolidatedActivities);

  const total: RowFormat = getTotalRow(consolidatedActivities, totalFinancials, type);

  return {
    activities: [...consolidatedActivities],
    alignedSum,
    total,
  };
};

export const getResultsByActivity = (
  cachedResults: CompanyAssessmentResults | undefined
): ResultsByActivity => {
  if (!cachedResults) return {};

  const allActivityResults: ActivityResults[] = [];

  cachedResults?.businessUnitResults.forEach((businessUnitResult) => {
    const generalAssessmentResult = businessUnitResult.generalAssessmentResult?.cachedResult;
    const activityResultsWithMSSG = businessUnitResult.activityResults.map((ar) => ({
      ...ar,
      isMSSGAligned: generalAssessmentResult?.isAligned && generalAssessmentResult.isCompleted,
    }));
    allActivityResults.push(...activityResultsWithMSSG);
  });

  return groupBy(allActivityResults, 'activityRef');
};

const hasAdaptationFinancials = (result: ActivityResults): boolean => {
  const financials = result?.cachedResult?.financials;
  if (financials?.capex?.adaptation === 0 && financials?.opex?.adaptation === 0) {
    return false;
  } else {
    return true;
  }
};

export const enablingTagGoesToAdaptation = ['1.1', '1.2', '1.3', '1.4', '2.1'];

const splitResultsBySC = (results: ActivityResults[]): ResultsBySC => {
  return results.reduce((acc, result) => {
    if (!result.cachedResult?.isAligned || !result.isMSSGAligned) {
      acc.notAligned = acc.notAligned ?? [];
      acc.notAligned.push(result);
      return acc;
    }
    const objectives =
      result?.cachedResult?.objectivesState?.substantialContributionObjectives?.toSorted();
    if (objectives.length === 1 && objectives.includes(ObjectiveKeyEnum.adaptation)) {
      if (result?.cachedResult?.activityTag === 'ENABLING') {
        acc.adaptationOnlyEnabling = acc.adaptationOnlyEnabling ?? [];
        acc.adaptationOnlyEnabling.push(result);
      } else {
        acc.adaptationOnly = acc.adaptationOnly ?? [];
        acc.adaptationOnly.push(result);
      }
    } else {
      if (objectives.includes(ObjectiveKeyEnum.adaptation) && hasAdaptationFinancials(result)) {
        if (result?.cachedResult?.activityTag === 'ENABLING') {
          if (enablingTagGoesToAdaptation.includes(result.activityRef)) {
            acc.adaptationEnabling = acc.adaptationEnabling ?? [];
            acc.adaptationEnabling.push(result);
          } else {
            acc[ObjectiveKeyEnum.adaptation] = acc[ObjectiveKeyEnum.adaptation] ?? [];
            acc[ObjectiveKeyEnum.adaptation].push(result);
          }
        } else {
          acc[ObjectiveKeyEnum.adaptation] = acc[ObjectiveKeyEnum.adaptation] ?? [];
          acc[ObjectiveKeyEnum.adaptation].push(result);
        }
      }
      let key = objectives
        .filter((o: ObjectiveKeyEnum) => o !== ObjectiveKeyEnum.adaptation)
        .join('-');
      if (
        result?.cachedResult?.activityTag === 'ENABLING' &&
        (!enablingTagGoesToAdaptation.includes(result.activityRef) ||
          !objectives.includes(ObjectiveKeyEnum.adaptation))
      ) {
        key = key + '-enabling';
      } else if (result?.cachedResult?.activityTag === 'TRANSITIONAL') {
        key = key + '-transitional';
      }
      acc[key] = acc[key] ?? [];
      acc[key].push(result);
    }
    return acc;
  }, {} as ResultsBySC);
};

export const getResultsByActivityAndSC = (
  results?: CompanyAssessmentResults
): ResultsByActivityAndSC => {
  const resultsByActivity = getResultsByActivity(results);
  const resultsByActivityAndTag: ResultsByActivityAndSC = {};

  Object.entries(resultsByActivity).forEach(([activityRef, activityResult]) => {
    resultsByActivityAndTag[activityRef] = splitResultsBySC(activityResult);
  });

  return resultsByActivityAndTag;
};

export const useTaxonomyTableData = (
  cAssessmentId: string,
  isGroup = false
): { data: TaxonomyTableData } => {
  const { company } = useCurrentCompany();
  const { data: results } = useCompanyAssessmentResults(cAssessmentId, false, isGroup);

  const nonMaterialFinancials = results?.nonMaterialFinancials;
  console.log('nonMaterialFinancials', nonMaterialFinancials);

  const resultsByActivityAndSC = useMemo(() => getResultsByActivityAndSC(results), [results]);

  const activities = useAllActivities();
  const activityMap = useMemo(
    () =>
      activities.reduce(
        (nameMap, activity) => ({
          ...nameMap,
          [activity.reference]: {
            name: activity.name,
            referenceNumber: activity.referenceNumber,
          },
        }),
        {}
      ),
    [activities]
  );

  const reportingYear = stringToYear(results?.startDate ?? '');

  const data = useMemo(() => {
    const totalFinancials: FinancialsPerType =
      results?.cachedResult?.financials ?? defaultFinancials;

    const turnoverSheet = calculateSheet(
      resultsByActivityAndSC,
      activityMap,
      totalFinancials,
      'revenue'
    );
    const capexSheet = calculateSheet(
      resultsByActivityAndSC,
      activityMap,
      totalFinancials,
      'capex'
    );
    const opexSheet = calculateSheet(resultsByActivityAndSC, activityMap, totalFinancials, 'opex');

    return {
      companyName: company?.name ?? '',
      reportingYear,
      currency: company?.currency ?? '',
      turnover: turnoverSheet,
      capex: capexSheet,
      opex: opexSheet,
      kpiTable: calculateKpiSheetData(
        turnoverSheet,
        capexSheet,
        opexSheet,
        totalFinancials,
        nonMaterialFinancials
      ),
    };
  }, [company, results, resultsByActivityAndSC, activityMap]);

  return { data };
};
